// Version gate cho `avatar init`: kiểm tra CLI local vs npm latest.
//
// Fail-soft design:
//   - Nếu không lấy được latest (offline / npm lỗi) → KHÔNG coi là outdated,
//     init vẫn chạy bình thường. Lỗi mạng không được chặn luồng init.
//
// Không dùng semver-compare phức tạp: triết lý đồng nhất với `avatar update` —
// "khác latest = nên update". Không phân biệt major/minor/patch vì:
//   - CLI thường có breaking change ngay cả minor.
//   - User luôn nên chạy bản mới nhất của dist-tag "latest".
//   - Rollback: team lùi dist-tag `latest` về bản cũ khi release hỏng ⇒ máy đã lên
//     bản hỏng (stable, lớn hơn) vẫn phải được nhắc.
//
// NGOẠI LỆ DUY NHẤT (F-18, phase 06d câu treo 4a = Y): `current` là PRERELEASE
// đi TRƯỚC `latest` theo SemVer §11 (vd 3.37.0-beta.2 vs 3.36.1) ⇒ không outdated.
// Người cài beta chủ động; bắt họ "update" về stable cũ hơn là hạ cấp. Stable đi
// trước `latest` KHÔNG được miễn (giữ đường rollback ở trên). Không parse được
// semver ⇒ so chuỗi như cũ.

import { compareParsedSemVer, parseSemVerLoose } from "./compare-semver-with-prerelease.js";
import { readCliVersion } from "./read-cli-version-from-package-json.js";
import { fetchVersionForDistTag } from "./self-update-cli-via-npm-global-install.js";

export interface VersionGateResult {
  /** true CHỈ khi lấy được latest VÀ latest !== current, trừ prerelease đi trước latest */
  outdated: boolean;
  current: string;
  /** null = không lấy được (offline/npm lỗi) */
  latest: string | null;
}

export function checkCliVersionGate(): VersionGateResult {
  const current = readCliVersion();
  const latest = fetchVersionForDistTag("latest");

  // Offline hoặc npm lỗi → fail-soft, không chặn init.
  if (latest === null) {
    return { outdated: false, current, latest: null };
  }

  // Khác latest → outdated, trừ prerelease đi trước latest (xem header).
  const outdated = latest !== current && !isPrereleaseAheadOf(current, latest);
  return { outdated, current, latest };
}

function isPrereleaseAheadOf(current: string, latest: string): boolean {
  const parsedCurrent = parseSemVerLoose(current);
  const parsedLatest = parseSemVerLoose(latest);
  if (parsedCurrent === null || parsedLatest === null) return false;
  if (parsedCurrent.prerelease === null) return false;
  return compareParsedSemVer(parsedCurrent, parsedLatest) > 0;
}
